import React, { useState, useEffect } from 'react';
import { getUserRequestHistory, getTenantRequestHistory, getAllRequestsHistory, getUsersByTenant, getAllTenants } from '../../services/api';
import { authService } from '../../services/auth';
import './Historico.css';

interface User {
  id: string;
  username: string;
  name: string;
  role: string;
  tenantId?: string;
  isTenantAdmin?: boolean;
}

interface RequestItemType {
  id: string;
  tipo: string;
  dataHora: string;
  creditosGastos: number;
  status: string;
  requisicao: any;
  resposta: any;
  usuario: string;
  user?: {
    id: string;
    username: string;
    name: string;
  };
  tenant?: {
    id: string;
    name: string;
  };
}

interface PaginationProps {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface FilterProps {
  startDate: string;
  endDate: string;
  queryType: string;
  userId: string;
  tenantId?: string;
}

// Função auxiliar para formatar data para o formato YYYY-MM-DD
const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Formatar a data para a API no formato ISO 8601 usado pelo Prisma/PostgreSQL
const formatDateForAPI = (dateString: string): string => {
  if (!dateString) return '';
  
  // Considerar que a data já vem no formato YYYY-MM-DD do input type="date"
  // Adicionar o horário para que seja um ISO timestamp completo
  if (dateString.includes('T')) {
    return dateString; // Já está em formato ISO
  }
  
  // Adicionar o timestamp para início ou fim do dia, dependendo do contexto
  if (dateString.length === 10) { // YYYY-MM-DD
    return `${dateString}T00:00:00.000Z`;
  }
  
  return dateString;
};

// Formatar data para exibição ao usuário no formato DD/MM/YYYY HH:MM
const formatDate = (dateString: string) => {
  if (!dateString || dateString === '' || dateString === 'undefined' || dateString === 'null') {
    return 'Data indisponível';
  }
  
  try {
    const date = new Date(dateString);
    
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }
    
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  } catch (error) {
    console.error('Erro ao formatar data:', dateString);
    return 'Data inválida';
  }
};

// Formatar status para exibição
const formatStatus = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return 'Concluído';
    case 'failed':
      return 'Falha';
    case 'processing':
      return 'Processando';
    case 'pending':
      return 'Pendente';
    default:
      return status || 'Desconhecido';
  }
};

// Obter data de hoje formatada para input
const getTodayFormatted = (): string => {
  return formatDateForInput(new Date());
};

// Obter data de 30 dias atrás formatada para input
const get30DaysAgoFormatted = (): string => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return formatDateForInput(date);
};

const Historico: React.FC = () => {
  const [requests, setRequests] = useState<RequestItemType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [pagination, setPagination] = useState<PaginationProps>({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });
  const [filters, setFilters] = useState<FilterProps>({
    startDate: '',
    endDate: '',
    queryType: '',
    userId: '',
    tenantId: ''
  });
  const [users, setUsers] = useState<{id: string, name: string}[]>([]);
  const [tenants, setTenants] = useState<{id: string, name: string}[]>([]);

  // Carregar usuário atual
  useEffect(() => {
    const user = authService.getUser();
    setCurrentUser(user);
    
    // Carregar tenants se for admin global
    if (user && user.role === 'admin' && !user.tenantId) {
      fetchAllTenants();
    }
  }, []);

  // Carregar histórico de consultas
  useEffect(() => {
    if (currentUser) {
      // console.log('Carregando histórico para usuário:', currentUser);
      fetchRequestHistory();
      
      // Carregar lista de usuários para o filtro se for admin do tenant
      if (currentUser.isTenantAdmin && currentUser.tenantId) {
        fetchTenantUsers(currentUser.tenantId);
      }
    }
  }, [pagination.page, pagination.limit, filters, currentUser]);

  const fetchTenantUsers = async (tenantId: string) => {
    try {
      // console.log(`Buscando usuários do tenant ${tenantId}`);
      const token = authService.getToken();
      // console.log('Token para requisição:', token ? 'Token disponível' : 'Token não disponível');
      
      // Usar axios em vez de fetch para manter consistência com o resto da aplicação
      const { getUsersByTenant } = await import('../../services/api');
      const result = await getUsersByTenant(tenantId);
      
      // console.log('Resultado da busca de usuários:', result);
      
      if (result.success && Array.isArray(result.data)) {
        setUsers(result.data.map((user: {id: string; name?: string; username?: string}) => ({
          id: user.id,
          name: user.name || user.username || 'Usuário'
        })));
        // console.log('Usuários carregados com sucesso:', result.data.length);
      } else {
        console.error('Formato de resposta inválido:', result);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários do tenant:', error);
    }
  };

  const fetchAllTenants = async () => {
    try {
      const response = await getAllTenants();
      if (response.success) {
        setTenants(response.data.map((tenant: any) => ({
          id: tenant.id,
          name: tenant.name
        })));
      }
    } catch (error) {
      console.error('Erro ao buscar tenants:', error);
    }
  };

  const fetchRequestHistory = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);

    try {
      let response;
      
      // Se for admin global, buscar todas as requisições
      if (currentUser.role === 'admin' && !currentUser.tenantId) {
        // console.log('Buscando histórico como admin global');
        // console.log('Filtros aplicados:', filters);
        response = await getAllRequestsHistory({
          page: pagination.page,
          limit: pagination.limit,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          queryType: filters.queryType || undefined,
          userId: filters.userId || undefined,
          tenantId: filters.tenantId || undefined
        });
        // console.log('Resposta do histórico completo:', response);
      }
      // Se o usuário for admin do tenant, buscar histórico de todas as consultas do tenant
      else if (currentUser.isTenantAdmin && currentUser.tenantId) {
        response = await getTenantRequestHistory(currentUser.tenantId, {
          page: pagination.page,
          limit: pagination.limit,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          queryType: filters.queryType || undefined,
          userId: filters.userId || undefined
        });
      } else {
        response = await getUserRequestHistory({
          page: pagination.page,
          limit: pagination.limit,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          queryType: filters.queryType || undefined
        });
      }

      if (response.success) {
        // Usar diretamente o array de requests da resposta
        const requestsData = response.requests || [];
        
        // Garantir que todos os objetos nas requisições estão devidamente formatados
        const formattedRequests = requestsData.map((request: any) => {
          // Se os dados já vierem formatados do backend (via getUserRequests), preservar a estrutura
          if (request.tipo && request.requisicao !== undefined && request.resposta !== undefined) {
            // console.log('Usando estrutura já formatada do backend');
            return {
              id: request.id,
              tipo: request.tipo || 'Desconhecido',
              dataHora: request.dataHora || null,
              creditosGastos: typeof request.creditosGastos === 'number' ? request.creditosGastos : 0,
              status: request.status || 'UNKNOWN',
              requisicao: request.requisicao || {},
              resposta: request.resposta || {},
              usuario: request.usuario || 'N/A',
              user: request.user || null,
              tenant: request.tenant || null
            };
          }
          
          // Se os dados vierem no formato bruto, processar manualmente
          // Garantir que os dados da requisição e da resposta sejam preservados
          let requisicao = {};
          try {
            requisicao = typeof request.requestData === 'string' 
              ? JSON.parse(request.requestData || '{}') 
              : (request.requestData || {});
          } catch (error) {
            console.error('Erro ao processar requestData:', error);
            requisicao = request.requestData || {};
          }
          
          let resposta = {};
          try {
            resposta = typeof request.responseData === 'string'
              ? JSON.parse(request.responseData || '{}')
              : (request.responseData || {});
          } catch (error) {
            console.error('Erro ao processar responseData:', error);
            resposta = request.responseData || {};
          }
          
          return {
            id: request.id,
            tipo: request.queryType?.name || 'Desconhecido',
            dataHora: request.createdAt || null,
            creditosGastos: request.creditsSpent !== undefined ? Number(request.creditsSpent) : 0,
            status: request.status || 'UNKNOWN',
            requisicao: requisicao,
            resposta: resposta,
            usuario: request.user?.name || request.user?.username || 'N/A',
            user: request.user || null,
            tenant: request.tenant || null
          };
        });
        
        // console.log('Requests formatados:', formattedRequests);
        setRequests(formattedRequests);
        
        // Usar diretamente os dados de paginação da resposta
        if (response.pagination) {
          setPagination(prevState => ({
            ...prevState,
            total: response.pagination.total || 0,
            page: response.pagination.page || 1,
            limit: response.pagination.limit || 10,
            totalPages: response.pagination.totalPages || 0
          }));
        }
      } else {
        setError(response.error || 'Erro ao carregar histórico de consultas');
      }
    } catch (error) {
      setError('Ocorreu um erro ao carregar o histórico');
      console.error('Erro ao buscar histórico:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setPagination((prevState: PaginationProps) => ({
        ...prevState,
        page: newPage
      }));
    }
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFilters((prevState: FilterProps) => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Resetar para a primeira página ao aplicar filtros
    setPagination((prevState: PaginationProps) => ({
      ...prevState,
      page: 1
    }));
  };

  // Renderizar detalhes baseado no tipo de consulta
  const renderRequestDetails = (request: RequestItemType) => {    
    // Verificar se temos dados reais da requisição e resposta
    const temDadosRequisicao = request.requisicao && Object.keys(request.requisicao).length > 0;
    const temDadosResposta = request.resposta && Object.keys(request.resposta).length > 0;
    
    // Status da requisição no banco vs status da resposta da API
    // Priorizar o status no banco caso seja FAILED
    const statusExibido = request.status === 'FAILED' ? 'Falha' : 
                         (temDadosResposta && typeof request.resposta.status === 'string' ? 
                           (request.resposta.status === 'failed' ? 'Falha' : 
                            request.resposta.status === 'success' ? 'Sucesso' : 
                            request.resposta.status) : 
                           formatStatus(request.status));
    
    switch (request.tipo) {
      case 'GetCID':
        return (
          <>
            <strong>IID:</strong> {
              temDadosRequisicao ? 
                (request.requisicao.iid ? request.requisicao.iid : 
                 request.requisicao.value ? request.requisicao.value : 
                 request.requisicao.raw ? request.requisicao.raw : 'N/A') : 
                'N/A'
            }<br />
            <strong>CID:</strong> {
              temDadosResposta ? 
                (request.resposta.cid ? request.resposta.cid : 
                 request.resposta.value ? request.resposta.value : 
                 request.resposta.raw ? request.resposta.raw : 'N/A') : 
                'N/A'
            }<br />
            <strong>Status:</strong> {statusExibido}
          </>
        );
      case 'PIDKey':
        // Verificar se temos detalhes da chave na resposta
        const temDetalhesChave = temDadosResposta && 
          ((request.resposta.keydata && request.resposta.keydata.length > 0) || 
           (request.resposta.keyInfo && request.resposta.keyInfo.length > 0));
        
        // Se o status é success mas não temos detalhes, pode ser um erro
        const statusFalho = request.status === 'FAILED' || 
          (request.resposta && request.resposta.status === 'failed') ||
          (request.resposta && request.resposta.status === 'success' && !temDetalhesChave);
            
        return (
          <>
            <strong>PIDKey:</strong> {
              temDadosRequisicao ? 
                (request.requisicao.pidkey ? request.requisicao.pidkey : 
                 request.requisicao.value ? request.requisicao.value : 
                 request.requisicao.raw ? request.requisicao.raw : 'N/A') : 
                'N/A'
            }<br />
            <strong>Status:</strong> {statusFalho ? 'Falha' : 'Sucesso'}<br />
            <strong>Description:</strong> {
              (request.resposta.keydata && request.resposta.keydata.length > 0 && request.resposta.keydata[0].Description) ||
              (request.resposta.keyInfo && request.resposta.keyInfo.length > 0 && request.resposta.keyInfo[0].Description) ||
              request.resposta.Description ||
              (temDadosResposta && !temDetalhesChave ? 'Chave inválida ou não reconhecida' : 'N/A')
            }<br />
            <strong>ErrorCode:</strong> {
              (request.resposta.keydata && request.resposta.keydata.length > 0 && request.resposta.keydata[0].ErrorCode) ||
              (request.resposta.keyInfo && request.resposta.keyInfo.length > 0 && request.resposta.keyInfo[0].ErrorCode) ||
              request.resposta.ErrorCode ||
              (temDadosResposta && !temDetalhesChave ? '0xC004C000' : 'N/A')
            }
          </>
        );
      case 'OCR':
        return (
          <>
            <strong>Status:</strong> {statusExibido}<br />
            <strong>Resultado:</strong> {
              temDadosResposta ? 
                (request.resposta.result ? request.resposta.result : 
                 request.resposta.value ? request.resposta.value : 
                 request.resposta.raw ? request.resposta.raw : 'N/A') : 
                'N/A'
            }
          </>
        );
      default:
        if (temDadosRequisicao) {
          // Se tivermos apenas um valor bruto ou uma string simples
          if (request.requisicao.value) {
            return (
              <>
                <strong>Valor enviado:</strong> {request.requisicao.value}<br />
                <strong>Resposta:</strong> {
                  temDadosResposta ? 
                    (request.resposta.value ? request.resposta.value : 
                     request.resposta.raw ? request.resposta.raw : 
                     JSON.stringify(request.resposta)) : 
                    'N/A'
                }<br />
                <strong>Status:</strong> {statusExibido}
              </>
            );
          }
          
          // Para outros formatos de dados
          return (
            <>
              <strong>Dados da Requisição:</strong><br />
              {Object.entries(request.requisicao).map(([key, value]) => (
                <span key={key}><strong>{key}:</strong> {
                  typeof value === 'object' ? JSON.stringify(value) : String(value)
                }<br /></span>
              ))}
              {temDadosResposta && (
                <>
                  <strong>Resposta:</strong><br />
                  {typeof request.resposta === 'string' ? 
                    request.resposta : 
                    Object.entries(request.resposta).map(([key, value]) => (
                      <span key={key}><strong>{key}:</strong> {
                        typeof value === 'object' ? JSON.stringify(value) : String(value)
                      }<br /></span>
                    ))
                  }
                </>
              )}
              <strong>Status:</strong> {statusExibido}
            </>
          );
        }
        return <span>Tipo de consulta não reconhecido</span>;
    }
  };

  return (
    <div className="historico-container">
      <h2>
        {currentUser?.role === 'admin' && !currentUser.tenantId
          ? 'Histórico Completo de Consultas'
          : currentUser?.isTenantAdmin 
            ? 'Histórico de Consultas do Cliente' 
            : 'Histórico de Consultas'}
      </h2>
      
      <div className="filters-container">
        <form onSubmit={handleFilterSubmit}>
          <div className="filter-group">
            <div className="filter-field">
              <label htmlFor="startDate">Data Inicial</label>
              <input
                type="date"
                id="startDate"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
              />
            </div>
            
            <div className="filter-field">
              <label htmlFor="endDate">Data Final</label>
              <input
                type="date"
                id="endDate"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
              />
            </div>
            
            <div className="filter-field">
              <label htmlFor="tipo">Tipo de Consulta</label>
              <select
                id="tipo"
                name="queryType"
                value={filters.queryType}
                onChange={handleFilterChange}
              >
                <option value="">Todos</option>
                <option value="GetCID">GetCID</option>
                <option value="PIDKey">PIDKey</option>
                <option value="OCR">OCR</option>
              </select>
            </div>
            
            {/* Filtro de tenant para administradores */}
            {currentUser?.role === 'admin' && !currentUser.tenantId && (
              <div className="filter-field">
                <label htmlFor="tenantId">Cliente</label>
                <select
                  id="tenantId"
                  name="tenantId"
                  value={filters.tenantId || ''}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos os clientes</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {currentUser?.isTenantAdmin && (
              <div className="filter-field">
                <label htmlFor="user">Usuário</label>
                <select
                  id="user"
                  name="userId"
                  value={filters.userId}
                  onChange={handleFilterChange}
                >
                  <option value="">Todos os usuários</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            <button type="submit">Filtrar</button>
          </div>
        </form>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">Carregando histórico de consultas...</div>
      ) : requests.length === 0 ? (
        <div className="empty-state">
          <p>Nenhuma consulta encontrada.</p>
          {Object.values(filters).some(value => value !== '') && (
            <p>Tente modificar os filtros para encontrar resultados.</p>
          )}
        </div>
      ) : (
        <>
          <div className="requests-table">
            <table>
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  {/* Mostrar coluna de tenant apenas para admin global */}
                  {currentUser?.role === 'admin' && !currentUser.tenantId && <th>Cliente</th>}
                  {/* Mostrar coluna de usuário para admins */}
                  {(currentUser?.isTenantAdmin || (currentUser?.role === 'admin' && !currentUser.tenantId)) && <th>Usuário</th>}
                  <th>Tipo</th>
                  <th>Detalhes</th>
                  <th>Créditos</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((request: RequestItemType) => (
                  <tr key={request.id}>
                    <td className="date-cell">{request.dataHora ? new Date(request.dataHora).toLocaleString() : 'Data indisponível'}</td>
                    {/* Mostrar coluna de tenant apenas para admin global */}
                    {currentUser?.role === 'admin' && !currentUser.tenantId && (
                      <td className="tenant-cell">{request.tenant?.name || 'N/A'}</td>
                    )}
                    {/* Mostrar coluna de usuário para admins */}
                    {(currentUser?.isTenantAdmin || (currentUser?.role === 'admin' && !currentUser.tenantId)) && (
                      <td className="user-cell">{request.usuario}</td>
                    )}
                    <td className="type-cell">{request.tipo}</td>
                    <td className="details-cell">{renderRequestDetails(request)}</td>
                    <td className="credits-cell">{request.creditosGastos !== undefined ? request.creditosGastos.toFixed(2) : '0.00'}</td>
                    <td className="status-cell">
                      <span className={`status-pill status-${
                        request.status === 'FAILED' ||
                        (request.resposta?.status === 'failed') ||
                        (request.tipo === 'GetCID' && request.resposta?.cid === 'N/A') ?
                        'failed' : 'success'}`
                      }>
                        {request.status === 'FAILED' ? 'Falha' :
                         (request.resposta?.status === 'failed' ? 'Falha' :
                         (request.tipo === 'GetCID' && request.resposta?.cid === 'N/A') ? 'Falha' : 'Sucesso')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(1)}
                disabled={pagination.page === 1}
              >
                &laquo;
              </button>
              
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
              >
                &lt;
              </button>
              
              <span className="page-info">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
              >
                &gt;
              </button>
              
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={pagination.page === pagination.totalPages}
              >
                &raquo;
              </button>
            </div>
          )}
          
          <div className="page-size">
            <label htmlFor="page-size-select">Itens por página:</label>
            <select
              id="page-size-select"
              value={pagination.limit}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPagination({
                ...pagination,
                limit: Number(e.target.value),
                page: 1 // Voltar para primeira página ao mudar o tamanho
              })}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
        </>
      )}
    </div>
  );
};

export default Historico; 